import fp from 'lodash/fp';
import clsx from 'clsx';
import React, { ComponentType, Consumer, SyntheticEvent, ElementType, MutableRefObject, FC } from 'react';
import { config } from '@factorin/container.macro';

import styles from './utils.module.scss';


export const { grabCursor } = styles;

export const animation: number = 300;

export const stopPropagation = (event: SyntheticEvent) =>
    event && event.stopPropagation();

export const withStopPropagation = (action: () => void) => (event: SyntheticEvent) => {
    stopPropagation(event);
    action();
};

export type FactorinCheckModel<TType = string> = {
    all: boolean;
    include: TType[];
    exclude: TType[];
};

export const DEFAULT_CHECK_MODEL: FactorinCheckModel<any> = {
    all: false,
    include: [],
    exclude: [],
};

export class FactorinCheckClass<TType = string> {
    defaultModel: FactorinCheckModel<TType> = {
        all: false,
        include: [],
        exclude: [],
    };

    getSingle = (
        value: TType,
        { all, include = [], exclude = [] }: FactorinCheckModel<TType>,
    ) =>
        all && !exclude.some(fp.isEqual(value)) || include.some(fp.isEqual(value));

    updateSingle = (
        value: TType,
        { all, include = [], exclude = [] }: FactorinCheckModel<TType>,
        checked: boolean,
    ) => ({
        all,
        include: !all
            ? checked
                ? [...include, value]
                : include.filter(i => !fp.isEqual(i, value))
            : include,
        exclude: all
            ? checked
                ? exclude.filter(i => !fp.isEqual(i, value))
                : [...exclude, value]
            : exclude,
    });

    getAll = (
        itemCount: number,
        { all, include = [], exclude = [] }: FactorinCheckModel<TType>,
    ) =>
        itemCount !== 0 && (all && exclude.length === 0 || include.length === itemCount);

    updateAll = (
        itemCount: number,
        { all, include = [], exclude = [] }: FactorinCheckModel<TType>,
    ) => ({
        all: all && exclude.length !== 0 ||
            !all && include.length < itemCount,
        include: [],
        exclude: [],
    });

    someSelected = (
        { all, include = [] }: FactorinCheckModel<TType>,
    ) => all || include.length > 0;
}

export const setHtml = (html: string) => ({
    dangerouslySetInnerHTML: { __html: html },
});

export const setRef = <TRef extends any>(ref: React.Ref<TRef>, value: TRef): void => {
    if (ref && fp.isFunction(ref))
        ref(value);
    if (ref && 'current' in ref) {
        const mutableRef = ref as MutableRefObject<TRef>;
        mutableRef.current = value;
    }
};

export const multipleRef = <TRef extends any>(...refs: React.Ref<TRef>[]) =>
    (value: TRef): void =>
        refs.forEach(ref => setRef(ref, value));

export const getDisplayName = (...args: ComponentType[]) => args
    .map(Component => Component.displayName || Component.name || 'Component')
    .join(', ');

export type Omit<TSource, KOmitted extends keyof TSource> =
    Pick<TSource, Exclude<keyof TSource, KOmitted>>;

export const contextHOC =
    <PContext extends {}>
    (Context: Consumer<PContext>) =>
        <PComponent extends PContext>(
            Component: FC<PComponent>,
        ): FC<Omit<PComponent, keyof PContext>> =>
            (props: Omit<PComponent, keyof PContext>) => (
                <Context>
                    {value => {
                        const allProps = { ...value, ...props } as PComponent;
                        return <Component {...allProps} />;
                    }}
                </Context>
            );

export type ClassName = { className?: string };

export const withClassName = <PComponent extends ClassName>(
    className: string,
    component: ReactAnyType<PComponent>,
): ComponentType<PComponent> => ({ className: additionalClassName, ...props }) =>
    React.createElement(component, {
        ...props,
        className: clsx(className, additionalClassName),
    } as PComponent);

export type Forbid<TType, TForbid> = TType & {
    [K in keyof TForbid]?: never;
};

export type ReactAnyType<PComponent> =
    ComponentType<PComponent> | ElementType<PComponent>;

export type ComponentWrapper<PComponent, PWrapper> = PWrapper & {
    component: ReactAnyType<Forbid<PComponent, PWrapper>>;
    componentRef?: React.Ref<any>;
};

export type ComponentWrapperProps<PComponent, PWrapper> =
    PComponent & ComponentWrapper<PComponent, PWrapper>;

export { clsx as cn };

type LocalStorageMappers<TItem> = {
    serialize: (item: TItem) => string;
    deserialize: (saved: string) => TItem;
    fallback?: TItem | (() => TItem);
};

export type BrowserStorageAccessors<TItem> = {
    get: () => TItem;
    set: (value: TItem) => void;
};

interface LocalStorageAccessor {
    <TItem extends any>(
        key: string,
        mappers: LocalStorageMappers<TItem>,
        useSessionStorage?: boolean,
    ): BrowserStorageAccessors<TItem>;
    (
        key: string,
        mappers?: Partial<LocalStorageMappers<string>>,
        useSessionStorage?: boolean,
    ): BrowserStorageAccessors<string>;
}

export const browserStorageAccessor: LocalStorageAccessor = <TItem extends any>(
    key: string,
    mappers?: Partial<LocalStorageMappers<TItem>>,
    useSessionStorage?: boolean,
) => {
    const storage = window[useSessionStorage ? 'sessionStorage' : 'localStorage'];
    return {
        get: fp.pipe(
            () => storage.getItem(key),
            serialized => serialized
                ? mappers && mappers.deserialize && mappers.deserialize(serialized)
                : mappers && fp.isFunction(mappers.fallback)
                    ? mappers.fallback()
                    : mappers && mappers.fallback || null,
        ),
        set: fp.pipe(
            mappers && mappers.serialize || fp.toString,
            (serialized: string) => storage.setItem(key, serialized),
        ),
    };
};

type Fallback<TItem> = TItem | (() => TItem);

function browserStorageMapper<TItem>(fallback: Fallback<TItem>) {
    return {
        serialize: JSON.stringify,
        deserialize: value => {
            try {
                return JSON.parse(value);
            } catch (error) {
                return fallback || null;
            }
        },
        fallback,
    };
}

export const localStorageAccessorJSON = <TItem extends any>(
    key: string,
    fallback?: Fallback<TItem>,
): BrowserStorageAccessors<TItem> =>
    browserStorageAccessor<TItem>(key, browserStorageMapper<TItem>(fallback));

export const sessionStorageAccessorJSON = <TItem extends any>(
    key: string,
    fallback?: Fallback<TItem>,
): BrowserStorageAccessors<TItem> =>
    browserStorageAccessor<TItem>(key, browserStorageMapper<TItem>(fallback), true);

export function addChildrenProps(children: React.ReactNode, props: object): React.ReactNode;
export function addChildrenProps(children: React.ReactNode[], props: object): React.ReactNode[];
export function addChildrenProps(children: React.ReactNode | React.ReactNode[], props: object) {
    const addProps = (child: React.ReactNode): React.ReactNode => React.isValidElement(child)
        ? React.cloneElement(child, props)
        : child;
    if (fp.isArray(children))
        return children.map(addProps);
    return addProps(children);
}

export const fileUrl = (url: string) => url.replace('./',
    `${config.basePath || ''}${config.pathPrefix || ''}/`);

export const scrollToTop = (top: number = 0) => {
    if ('scrollTo' in window) {
        window.scrollTo({
            top,
            behavior: 'smooth',
        });
    } else
        window.scroll(0, top);
};
